import React, { PropsWithChildren, useEffect } from 'react'
import Box from '@material-ui/core/Box'
import { makeStyles } from '@material-ui/core/styles'
import { Switch, Route, useHistory } from 'react-router-dom'
import { getValue } from 'remote-config-value'
import { EsimProvider } from 'providers'
import ProcessEsim from './components/processPlans/Index'
import Article from './components/howTo/articles/Article'
import AppContextProvider, { useAppContext } from '../../contexts/AppContext'
import { RouterProps } from '../../utils/objects'
import GoBack from '../../components/GoBack'
import Content from './components/Content'
import Videos from './components/howTo/videos/Index'
import Articles from './components/howTo/articles/Index'
import Header from './components/headers/Index'
import { handleEsimGTM } from '../../utils/utils'
import { useLocation } from 'react-router-dom'

const useStyles = makeStyles(theme => ({
    root: {
        overflowY: 'auto',
        overflowX: 'hidden',
        '& *': {
            fontFamily: 'Inter, Montserrat, sans-serif !important'
        },
        [theme.breakpoints.up('lg')]: {
            maxWidth: '1050px',
            margin: 'auto'
        }
    },
    detailsContainer: {
        padding: '3rem',
        height: '100%',
        minHeight: '700px',
        [theme.breakpoints.down('lg')]: {
            padding: '1.5rem 3rem'
        }
    }
}))

interface DetailsInterface {
    routeProps: RouterProps
}

const Wrapper = ({ children }: PropsWithChildren<Record<string, any>>) => {
    const classes = useStyles()

    return <Box className={classes.root}>
        <Box className={classes.detailsContainer}>
            {children}
        </Box>
    </Box>
}

const AppDetails = ({ routeURL: url }: { routeURL: string }) => {
    const { app } = useAppContext()
    const location = useLocation()

    const renderRoutes = (routes: JSX.Element) => {
        let Provider: React.ComponentType<{ children: JSX.Element }> | undefined
        switch (app.identifier?.toLowerCase()) {
                case getValue('esim_app_identifier')?.toLowerCase():
                    Provider = EsimProvider
                    break
                default:
                    break
        }

        if (Provider) {
            return (
                <Provider>
                    {routes}
                </Provider>
            )
        }

        return routes
    }

    useEffect(() => {
        if (location.pathname.endsWith('/apps-marketplace/prosim')) {
            handleEsimGTM(app, {
                event: 'PDC_View_Full_Description',
                other: {
                    ecommerce: {
                        items: [{ id: app.id, name: app.title, item_name: 'ProSIM' }]
                    }
                }
            })
        }
    }, [])

    return renderRoutes(
        <Switch>
            <Route path={`${url}/stepper`} render={() => {
                return <Wrapper><ProcessEsim /></Wrapper>
            }} />
            <Route path={`${url}/how-to-articles/:articleTitle`} render={() => {
                return <Wrapper><Article /></Wrapper>
            }} />
            <Route path={`${url}/how-to-articles`} render={() => {
                return <Wrapper><Articles /></Wrapper>
            }} />
            <Route path={`${url}/how-to-videos`} render={() => {
                return <Wrapper><Videos /></Wrapper>
            }} />
            <Route path={'/'} render={() => {
                return (
                    <>
                        <Header />
                        <Wrapper>
                            <Content />
                        </Wrapper>
                    </>
                )
            }} />
        </Switch>

    )
}

/**
 *
 */
const Index = (props: DetailsInterface) => {
    const url = props.routeProps.match.url.replace(/\/$/, '')
    const appURI = props.routeProps?.match.params.appName || ''
    const history = useHistory()

    useEffect(() => {
        const basePath = window.location.pathname
        const urlParams = new URLSearchParams(window.location.search)
        let redirectTo = urlParams.get('redirectTo')
        const item = urlParams.get('item')
        if (item) redirectTo = `${redirectTo}/${item}`
        if (redirectTo) history.replace(`${basePath}/${redirectTo}`)
    }, [])

    return (
        <AppContextProvider appURI={appURI}>
            <>
                <GoBack />
                <AppDetails routeURL={url} />
            </>
        </AppContextProvider>
    )
}

export default Index
